// Copyright 2026 Hardfin, Inc.

/**
 * API module for searching brands via Logo.dev integration.
 * Provides typeahead functionality for company name suggestions.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';

/**
 * Type definition for the API response wrapper.
 */
interface ApiResponse {
  data: unknown;
  metadata: { requestId: string };
}

/**
 * Schema for a single brand search result.
 */
const brandResultSchema = z.object({
  name: z.string(),
  domain: z.string(),
  description: z.string().nullable(),
  blurhash: z.string().nullable(),
});

/**
 * Schema for the brand search API response.
 */
const brandSearchResponseSchema = z.object({
  results: z.array(brandResultSchema),
});

/**
 * Type definition for a brand search result.
 */
export type BrandResult = z.infer<typeof brandResultSchema>;

/**
 * Minimum query length before searching.
 */
export const minQueryLength = 2;

/**
 * Searches for brands matching the query.
 * @param query - The search query string
 * @returns Array of brand results
 */
export async function searchBrands(query: string): Promise<BrandResult[]> {
  if (query.length < minQueryLength) {
    return [];
  }

  const url = '/v1/brand/search';
  const response = await api.get<ApiResponse>(url, {
    params: { q: query },
  });

  const parsed = await parseResponse(
    brandSearchResponseSchema,
    response.data.data,
    {
      schemaName: 'BrandSearchResponse',
      endpoint: url,
    }
  );
  return parsed.results;
}
